import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import "react-dates/initialize"
import * as React from "react"
import type Moment from "moment-timezone"
import moment from "moment-timezone"
import * as uuid from "uuid"
import TetherDatetime from "./components/TetherDatetime"
import { AntdIconWrap } from "../Icons/AntdIconWrap"

type Props = {
    style?: Record<string, any>
    disabled?: boolean
    value?: Maybe<Moment>
    defaultValue?: Maybe<Moment>
    placeholder?: string
    format: string
    roundMinutesTo: number
    onChange: (time: Maybe<Moment>, timeString: string) => void
}
type State = {
    opened: boolean
    inputValue: string
    value: Maybe<Moment>
    closeIconHovered: boolean
    timezone: string
}

/** @deprecated Use the TimePicker from '../uiKit' folder instead */
class TimePickerDeprecated extends React.PureComponent<Props, State> {
    inputId = uuid.v4()
    inputRef = React.createRef()
    pickerRef = React.createRef()
    static defaultProps = {
        style: {},
        disabled: false,
        format: "h:mm a",
        placeholder: "h:mm a",
        roundMinutesTo: 1,
        onChange: () => {},
    }

    roundMinutes = (time: Moment) => {
        const roundedMinutes = time.get("minutes") - (time.get("minutes") % this.props.roundMinutesTo)
        return time.set({
            minutes: roundedMinutes,
        })
    }

    getInitialInputValue = () => {
        const defaultValue = this.props.defaultValue

        if (defaultValue && moment.isMoment(defaultValue)) {
            const roundedValue = this.roundMinutes(defaultValue)
            return roundedValue.format(this.props.format)
        } else if (this.props.value && moment.isMoment(this.props.value)) {
            return this.props.value.format(this.props.format)
        } else {
            return ""
        }
    }
    getInitialTimezone = () => {
        let tz = null

        if (this.props.defaultValue && moment.isMoment(this.props.defaultValue)) {
            tz = this.props.defaultValue.tz()
        } else if (this.props.value && moment.isMoment(this.props.value)) {
            tz = this.props.value.tz()
        }

        return tz || moment.tz.guess()
    }
    state = {
        closeIconHovered: false,
        opened: false,
        value: this.props.defaultValue || this.props.value || null,
        inputValue: this.getInitialInputValue(),
        timezone: this.getInitialTimezone(),
    }

    componentDidMount() {
        this.inputId = uuid.v4()
        document.addEventListener("click", this.handleClickOutside, true)
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.handleClickOutside, true)
    }

    handleClickOutside = (event: any) => {
        if (this.state.opened) {
            const domNode = this.inputRef.current
            const pickerNode = this.pickerRef.current

            // @ts-expect-error
            if ((!domNode || !domNode.contains(event.target)) && (!pickerNode || !pickerNode.contains(event.target))) {
                // Normalize input value on blur/close
                this.handleNormalizeInputAndClose()
            }
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (!prevProps.value && this.props.value) {
            this.setValues(this.props.value)
        }

        if (prevProps.format !== this.props.format) {
            this.setState({
                inputValue: this.getInputValue(this.state.value),
            })
        }
    }

    handleGetOldValue = (newTimezone: Maybe<string>) => {
        if (this.props.value) {
            return moment.tz(this.props.value, this.props.format, false, newTimezone)
        } else if (this.props.defaultValue) {
            return moment.tz(this.props.defaultValue, this.props.format, false, newTimezone)
        } else {
            return moment.tz(newTimezone)
        }
    }
    setValues = (momentValue: Maybe<Moment>, setStateCallback: Maybe<() => void> = null) => {
        let newValue = null
        let newTimezone = null

        if (momentValue && moment.isMoment(momentValue)) {
            newTimezone = this.getInitialTimezone()
            const oldValue = this.handleGetOldValue(newTimezone)
            newValue = moment.tz(momentValue, this.props.format, false, newTimezone).set({
                year: oldValue.get("year"),
                month: oldValue.get("month"),
                date: oldValue.get("date"),
            })
        }

        this.setState(
            // @ts-expect-error
            {
                value: newValue,
                inputValue: this.getInputValue(newValue),
                ...(newTimezone
                    ? {
                          timezone: newTimezone,
                      }
                    : {}),
            },
            () => (setStateCallback ? setStateCallback() : false)
        )
    }
    handleOnChange = (value: Maybe<Moment>) => {
        this.setValues(moment(value))
    }
    handleOnClose = () => {
        this.setState({
            opened: false,
        })

        if (this.props.onChange) {
            const timeString = this.getInputValue(this.state.value)
            this.props.onChange(this.state.value, timeString)
        }
    }
    handleNormalizeInputAndClose = () => {
        // Normalize input value on blur/close
        if (this.state.value && this.state.inputValue.length > 0) {
            if (moment(this.state.inputValue, this.props.format, false).isValid()) {
                this.setState(
                    {
                        inputValue: this.getInputValue(this.state.value),
                    },
                    () => this.handleOnClose()
                )
            } else {
                this.setValues(moment.tz(this.state.timezone), this.handleOnClose)
            }
        } else {
            this.handleOnClose()
        }
    }
    //
    // Input
    //
    getInputValue = (value: Maybe<Moment>) => {
        if (value) {
            return value.format(this.props.format)
        } else {
            return ""
        }
    }
    handleInputOnChange = (e: any) => {
        const oldValue = this.handleGetOldValue(this.state.timezone)

        if (moment(e.target.value, this.props.format, false).isValid()) {
            const newValue = moment.tz(e.target.value, this.props.format, false, this.state.timezone).set({
                year: oldValue.get("year"),
                month: oldValue.get("month"),
                date: oldValue.get("date"),
            })
            const roundedValue = newValue.set({
                minutes: newValue.get("minutes") - (newValue.get("minutes") % this.props.roundMinutesTo),
            })
            this.setState({
                inputValue: e.target.value,
                value: roundedValue,
            })
        } else {
            const roundedValue = moment()
                .tz(this.state.timezone)
                .set({
                    year: oldValue.get("year"),
                    month: oldValue.get("month"),
                    date: oldValue.get("date"),
                })
            this.setState({
                inputValue: e.target.value,
                value: roundedValue,
            })
        }
    }
    handleInputOnFocus = (e: any) => {
        const target = e.currentTarget

        if (!this.state.value) {
            this.setValues(moment.tz(this.state.timezone), () => {
                this.setState({
                    opened: true,
                })

                if (target) {
                    target.select()
                }
            })
        } else {
            this.setState({
                opened: true,
            })

            if (target) {
                target.select()
            }
        }
    }
    handleOnResetValue = () => {
        this.setValues(null)

        if (this.props.onChange) {
            const timeString = this.getInputValue(null)
            this.props.onChange(null, timeString)
        }
    }
    renderCloseIcon = () => {
        return (
            <div
                onMouseEnter={() =>
                    this.setState({
                        closeIconHovered: true,
                    })
                }
                onMouseLeave={() =>
                    this.setState({
                        closeIconHovered: false,
                    })
                }
                className="close-icon-wrapper">
                {!this.state.closeIconHovered ? (
                    <AntdIconWrap
                        type="clock-circle"
                        theme="outlined"
                        style={{
                            opacity: "0.7",
                            marginRight: "0",
                        }}
                    />
                ) : (
                    <AntdIconWrap
                        type="close-circle"
                        theme="filled"
                        onClick={this.handleOnResetValue}
                        style={{
                            opacity: "0.7",
                            marginRight: "0",
                            cursor: "pointer",
                        }}
                    />
                )}
            </div>
        )
    }
    handleRenderInput = (props: any) => {
        return (
            // @ts-expect-error
            <div className={`input-wrapper${this.props.disabled ? " disabled" : ""}`} ref={this.inputRef}>
                <input
                    {...props}
                    className="form-control"
                    value={this.state.inputValue}
                    onFocus={this.handleInputOnFocus}
                    disabled={this.props.disabled}
                    onChange={this.handleInputOnChange}
                    onKeyDown={e => {
                        const keyCode = e.keyCode || e.which

                        if (keyCode === 9) {
                            this.handleNormalizeInputAndClose()
                        }
                    }}
                    placeholder={this.props.placeholder}
                    id={this.inputId}
                    key="timepicker-input"
                    type="text"
                />
                {this.renderCloseIcon()}
            </div>
        )
    }

    render() {
        return (
            <div className="time-picker" style={{ ...this.props.style }}>
                <TetherDatetime
                    // @ts-expect-error
                    pickerRef={this.pickerRef}
                    dateFormat={false}
                    open={this.state.opened}
                    value={this.state.value || moment.tz(this.state.timezone)}
                    utc={false}
                    defaultValue={this.props.defaultValue}
                    onChange={this.handleOnChange}
                    strictParsing={false}
                    timeFormat={this.props.format}
                    renderInput={this.handleRenderInput}
                    timeConstraints={{ minutes: { min: 0, max: 59, step: this.props.roundMinutesTo } }}
                />
            </div>
        )
    }
}

export default TimePickerDeprecated
